import * as React from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { FC, useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { BaseTextField } from '@user/components/Formik'
import { Field, useFormikContext } from 'formik'
import { ManageDefaultSalesTeamsFormModel } from './typings'
import { selectAllBranches } from '@user/store/branches/slice'
import { selectAllSalesTeams } from '@user/store/salesTeam/slice'
import { SideDrawerForm } from '@sales-center/components'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'

const getStyles = (theme: Theme) => ({
  label: {
    fontFamily: 'Source Sans Pro Semibold',
    marginBottom: theme.spacing(1),
  },

  title: {
    fontFamily: 'Source Sans Pro Semibold',
    fontSize: '16px',
    marginBottom: theme.spacing(1),
  },
})

interface Props {
  onCancel: () => void
}

export const ManageDefaultsFormFields: FC<Props> = (props: Props) => {
  const { onCancel } = props
  const [disableSave, setDisableSave] = useState<boolean>(true)
  const { dirty, isValid, isValidating, isSubmitting, submitForm } =
    useFormikContext<ManageDefaultSalesTeamsFormModel>()

  const salesTeams = useSelector(selectAllSalesTeams)
  const branches = useSelector(selectAllBranches)

  // Hide default sales team form fields
  const hideCompanyDefaultSalesTeam = true

  const branchesInUse = salesTeams.map((team) => team.branchId)
  const filteredBranches = branches.filter((branch) =>
    branchesInUse.includes(branch.id),
  )

  useEffect(() => {
    setDisableSave(!dirty || !isValid || isValidating || isSubmitting)
  }, [dirty, isValid, isValidating, isSubmitting])

  const onSave = useCallback(() => {
    submitForm()
  }, [submitForm])
  const styles = getStyles(useTheme())
  return (
    <SideDrawerForm
      disableSave={disableSave}
      onCancel={onCancel}
      onSave={onSave}
      primaryBtnLabel="Save"
    >
      <Box p={2}>
        <Box mb={3}>
          <Typography sx={styles.title}>Select Default Sales Teams</Typography>
          <Typography>
            Designate a default Sales Team for each branch. This will ensure the
            preferred Sales Team is selected when reviewing pending leads and/or
            creating new opportunities. If no default is provided, then a Sales
            Team will not be pre-selected.
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {!hideCompanyDefaultSalesTeam && (
            <Grid item xs={12}>
              <Typography sx={styles.label}>
                Company Default Sales Team
              </Typography>
              <Field
                name="companyDefaultSalesTeamId"
                label="Sales Team"
                component={BaseTextField}
                select
                required
              >
                <MenuItem value={-1}>No Default</MenuItem>
                {salesTeams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
              </Field>
              <Box mt={3}>
                <Divider />
              </Box>
            </Grid>
          )}

          {filteredBranches.map((branch) => (
            <Grid key={branch.id} item xs={12}>
              <Typography sx={styles.label}>{branch.name}</Typography>
              <Field
                name={`branchDefaults[branch_${branch.id}_salesTeam]`}
                label="Sales Team"
                component={BaseTextField}
                select
                required
              >
                <MenuItem value={-1}>No Default</MenuItem>
                {salesTeams
                  .filter((x) => x.branchId === branch.id)
                  .map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
              </Field>
            </Grid>
          ))}
        </Grid>
      </Box>
    </SideDrawerForm>
  )
}

ManageDefaultsFormFields.displayName = 'ManageDefaultsFormFields'
